import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, Divider,
  IconButton, Menu, MenuItem, TextField, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import PsfDashboard from './pages/PsfDashboard';
import { GlobalCalculatorSettingsProvider } from '../super-calculator/context/GlobalCalculatorSettingsContext';
import {
  clearPublicPsfSession,
  configurePublicPsfSession,
  restorePublicPsfSession,
  setPublicPsfContext,
  trackPublicPsfResponse,
  publicPsfApiUrl,
  withNgrokBrowserBypass,
  withNgrokBrowserBypassHeaders,
} from './services/api';
import { useTheme } from '../theme/ThemeProvider';
import HuttonsWhite from '../img/Huttons_white.png';
import HuttonsBlue from '../img/Huttons_blue.png';
import { buildAgentPhotoUrl } from '../super-picker-public/services/superPickerPublicApi';

async function readJsonResponse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') || '';
  const body = await response.text();
  let payload: any = null;
  try {
    payload = body ? JSON.parse(body) : null;
  } catch {
    const preview = body.replace(/\s+/g, ' ').slice(0, 160);
    throw new Error(
      `Personalized PSF API returned non-JSON (${response.status}, ${contentType || 'unknown content type'}).${preview ? ` Response: ${preview}` : ''}`,
    );
  }
  return payload;
}

type Bootstrap = {
  uuid: string;
  serverNow: string;
  status: 'NOT_STARTED' | 'ACTIVE' | 'EXPIRED';
  minutesUntilAccessStart: number;
  minutesUntilEventStart: number;
  minutesUntilExpiry: number;
  event: {
    id: number;
    name: string;
    startAt: string;
    endAt: string;
    accessStart: string;
    expiresAt: string;
  };
  consumer: { name: string; mobileMasked?: string | null };
  otpSent: boolean;
  otpExpiresAt: string | null;
  sessionValid?: boolean;
  activatedAt: string | null;
  invitedBy: { ceaNumber: string; name: string | null; email: string | null; mobile: string | null; photo: string | null };
};

const PUBLIC_PSF_ACTIVE_UUID_KEY = 'personalized-psf-active-share-uuid';

function readPublicPsfUuid(): { uuid: string; fromQuery: boolean } {
  if (typeof window === 'undefined') return { uuid: '', fromQuery: false };
  const queryUuid = new URLSearchParams(window.location.search).get('uuid')?.trim() || '';
  if (queryUuid) return { uuid: queryUuid, fromQuery: true };
  try {
    return { uuid: sessionStorage.getItem(PUBLIC_PSF_ACTIVE_UUID_KEY)?.trim() || '', fromQuery: false };
  } catch {
    return { uuid: '', fromQuery: false };
  }
}

function persistPublicPsfUuid(uuid: string): void {
  try { sessionStorage.setItem(PUBLIC_PSF_ACTIVE_UUID_KEY, uuid); } catch { /* privacy mode */ }
}

function clearPersistedPublicPsfUuid(uuid?: string): void {
  try {
    const current = sessionStorage.getItem(PUBLIC_PSF_ACTIVE_UUID_KEY);
    if (!uuid || current === uuid) sessionStorage.removeItem(PUBLIC_PSF_ACTIVE_UUID_KEY);
  } catch { /* privacy mode */ }
}

function cleanPublicPsfUrl(): void {
  if (typeof window === 'undefined' || !window.location.search) return;
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.hash}`);
}

function formatRemaining(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${String(hours).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function PublicPersonalizedPsfApp() {
  const [data, setData] = useState<Bootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dataRef = useRef<Bootstrap | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const publicSessionTokenRef = useRef<string | null>(null);
  const [serverClockNow, setServerClockNow] = useState<number | null>(null);
  const serverClockRef = useRef<{ serverNowMs: number; receivedAt: number } | null>(null);
  const [boundaryCheckPending, setBoundaryCheckPending] = useState(false);
  const boundaryCheckRef = useRef<{ access: boolean; expiry: boolean; retry: number }>({ access: false, expiry: false, retry: 0 });
  const { currentTheme, setTheme } = useTheme();
  const darkMode = currentTheme === 'dark';
  const [{ uuid, fromQuery }] = useState(readPublicPsfUuid);

  useEffect(() => {
    setPublicPsfContext(true);
    const restored = uuid ? restorePublicPsfSession(uuid) : null;
    if (restored) {
      publicSessionTokenRef.current = restored.sessionToken;
      setSessionToken(restored.sessionToken);
    }
    return () => {
      // Clear only the in-memory context when leaving the page. The persisted
      // token is intentionally retained so a refresh can restore the public
      // event session, matching Super Picker share-link behavior.
      setPublicPsfContext(false);
    };
  }, [uuid]);

  useEffect(() => {
    const handleServerTime = (event: Event) => {
      const serverNow = (event as CustomEvent<{ serverNow?: string }>).detail?.serverNow;
      const serverNowMs = Date.parse(String(serverNow || ''));
      if (!Number.isFinite(serverNowMs)) return;
      serverClockRef.current = { serverNowMs, receivedAt: performance.now() };
      setServerClockNow(serverNowMs);
    };
    window.addEventListener('public-psf-server-time', handleServerTime);
    return () => window.removeEventListener('public-psf-server-time', handleServerTime);
  }, []);

  useEffect(() => {
    const handleEventStatus = (event: Event) => {
      const code = (event as CustomEvent<{ code?: string }>).detail?.code;
      if (code !== 'EVENT_NOT_STARTED' && code !== 'EVENT_EXPIRED') return;
      if (code === 'EVENT_EXPIRED') clearPersistedPublicPsfUuid(uuid);
      clearPublicPsfSession(uuid);
      publicSessionTokenRef.current = null;
      setPublicPsfContext(true);
      setSessionToken(null);
      setData((current) => current ? { ...current, status: code === 'EVENT_EXPIRED' ? 'EXPIRED' : 'NOT_STARTED' } : current);
    };
    window.addEventListener('public-psf-event-status', handleEventStatus);
    return () => window.removeEventListener('public-psf-event-status', handleEventStatus);
  }, [uuid]);

  useEffect(() => {
    const handleSessionRequired = () => {
      clearPublicPsfSession(uuid);
      publicSessionTokenRef.current = null;
      setPublicPsfContext(true);
      setSessionToken(null);
      setOtpSent(false);
      setOtpError('Please verify your phone to continue.');
    };
    window.addEventListener('public-psf-session-required', handleSessionRequired);
    return () => window.removeEventListener('public-psf-session-required', handleSessionRequired);
  }, [uuid]);

  useEffect(() => {
    if (!resendAvailableAt) return undefined;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
      setResendSeconds(remaining);
      if (remaining === 0) setResendAvailableAt(null);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [resendAvailableAt]);

  const refreshBootstrap = useCallback(async (): Promise<Bootstrap | null> => {
    if (!uuid) { setError('Invalid share link'); return null; }
    try {
      const restored = restorePublicPsfSession(uuid);
      if (restored) {
        publicSessionTokenRef.current = restored.sessionToken;
        setSessionToken(restored.sessionToken);
      }
      const headers = withNgrokBrowserBypassHeaders();
      if (publicSessionTokenRef.current) headers.set('x-event-session', publicSessionTokenRef.current);
      const response = trackPublicPsfResponse(await fetch(withNgrokBrowserBypass(publicPsfApiUrl(`/api/super-personalized-psf-public/share/${encodeURIComponent(uuid)}`)), {
        headers,
      }));
      const payload = await readJsonResponse(response);
      if (!response.ok) {
        const requestError = Object.assign(new Error(payload.error || 'Invalid link'), { code: payload.code });
        if (['SHARE_NOT_FOUND', 'SHARE_DEFUNCT', 'EVENT_EXPIRED'].includes(String(payload.code))) {
          clearPersistedPublicPsfUuid(uuid);
          clearPublicPsfSession(uuid);
        }
        throw requestError;
      }
      persistPublicPsfUuid(uuid);
      if (fromQuery) cleanPublicPsfUrl();
      if (publicSessionTokenRef.current && payload.sessionValid === false) {
        clearPublicPsfSession(uuid);
        publicSessionTokenRef.current = null;
        setSessionToken(null);
      }
      dataRef.current = payload as Bootstrap;
      const serverNowMs = Date.parse(String(payload.serverNow || ''));
      if (Number.isFinite(serverNowMs)) {
        serverClockRef.current = { serverNowMs, receivedAt: performance.now() };
        setServerClockNow(serverNowMs);
      }
      setOtpSent(Boolean((payload as Bootstrap).otpSent));
      setOtpExpiresAt((payload as Bootstrap).otpExpiresAt || null);
      const otpExpiry = Date.parse(String((payload as Bootstrap).otpExpiresAt || ''));
      if ((payload as Bootstrap).otpSent && Number.isFinite(otpExpiry)) {
        setResendAvailableAt(Math.max(Date.now(), otpExpiry - 9 * 60 * 1000));
      }
      setData(payload as Bootstrap);
      setError(null);
      return payload as Bootstrap;
    } catch (err: any) {
      if (!dataRef.current) setError(err.message || 'Unable to load this share link');
      return null;
    }
  }, [fromQuery, uuid]);

  useEffect(() => { void refreshBootstrap(); }, [refreshBootstrap]);

  useEffect(() => {
    if (!serverClockRef.current) return undefined;
    const update = () => {
      const anchor = serverClockRef.current;
      if (anchor) setServerClockNow(anchor.serverNowMs + (performance.now() - anchor.receivedAt));
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [data?.serverNow]);

  const nowMs = serverClockNow ?? (data ? Date.parse(data.serverNow) : 0);
  const accessStartMs = data ? Date.parse(data.event.accessStart) : Number.POSITIVE_INFINITY;
  const eventStartMs = data ? Date.parse(data.event.startAt) : Number.POSITIVE_INFINITY;
  const eventEndMs = data ? Date.parse(data.event.endAt) : Number.POSITIVE_INFINITY;
  const expiryMs = data ? Date.parse(data.event.expiresAt) : Number.POSITIVE_INFINITY;
  const beforeAccess = Boolean(data && data.status !== 'EXPIRED' && (data.status === 'NOT_STARTED' || nowMs < accessStartMs));
  const expired = Boolean(data?.status === 'EXPIRED');
  const beforeStart = nowMs < eventStartMs;
  const eventEnded = nowMs >= eventEndMs;
  const verified = Boolean(sessionToken);
  const eventCountdown = data && beforeStart ? formatRemaining(eventStartMs - nowMs) : null;
  const accessOpenCountdown = data && beforeAccess ? formatRemaining(accessStartMs - nowMs) : null;
  const accessCountdown = expired ? null : formatRemaining(expiryMs - nowMs);

  const runBoundaryCheck = useCallback(async (kind: 'access' | 'expiry') => {
    const state = boundaryCheckRef.current;
    const payload = await refreshBootstrap();
    if (!payload) {
      if (state.retry < 3) {
        const delay = Math.min(10000, 2000 * 2 ** state.retry);
        state.retry += 1;
        window.setTimeout(() => { void runBoundaryCheck(kind); }, delay);
      } else {
        setBoundaryCheckPending(false);
      }
      return;
    }
    state.retry = 0;
    if (kind === 'access') {
      if (payload.status === 'ACTIVE') setBoundaryCheckPending(false);
      else if (payload.status === 'NOT_STARTED') {
        state.access = false;
        setBoundaryCheckPending(false);
      }
    } else if (payload.status === 'ACTIVE') {
      state.expiry = false;
      setBoundaryCheckPending(false);
    } else if (payload.status === 'EXPIRED') {
      clearPublicPsfSession(uuid);
      publicSessionTokenRef.current = null;
      setPublicPsfContext(true);
      setSessionToken(null);
      setBoundaryCheckPending(false);
    }
  }, [refreshBootstrap, uuid]);

  useEffect(() => {
    if (!data) return undefined;
    const state = boundaryCheckRef.current;
    const accessBoundaryReached = data.status === 'NOT_STARTED' && nowMs >= accessStartMs;
    const expiryBoundaryReached = data.status !== 'EXPIRED' && nowMs >= expiryMs;
    if (accessBoundaryReached && !state.access) {
      state.access = true;
      state.retry = 0;
      setBoundaryCheckPending(true);
      void runBoundaryCheck('access');
    }
    if (expiryBoundaryReached && !state.expiry) {
      state.expiry = true;
      state.retry = 0;
      setBoundaryCheckPending(true);
      void runBoundaryCheck('expiry');
    }
    return undefined;
  }, [accessStartMs, data, expiryMs, nowMs, runBoundaryCheck]);

  const handleStatusError = useCallback((payload: any) => {
    if (payload?.code !== 'EVENT_NOT_STARTED' && payload?.code !== 'EVENT_EXPIRED') return;
    if (payload.code === 'EVENT_EXPIRED') clearPersistedPublicPsfUuid(uuid);
    clearPublicPsfSession(uuid);
    publicSessionTokenRef.current = null;
    setPublicPsfContext(true);
    setSessionToken(null);
    setOtpSent(false);
    setData((current) => current ? { ...current, status: payload.code === 'EVENT_EXPIRED' ? 'EXPIRED' : 'NOT_STARTED' } : current);
  }, [uuid]);
  const sendOtp = useCallback(async () => {
    setOtpError(null);
    try {
      const response = trackPublicPsfResponse(await fetch(withNgrokBrowserBypass(publicPsfApiUrl(`/api/super-personalized-psf-public/share/${encodeURIComponent(uuid)}/otp/send`)), {
        method: 'POST',
        headers: withNgrokBrowserBypassHeaders(),
      }));
      const payload = await readJsonResponse(response);
      if (!response.ok) throw Object.assign(new Error(payload.error || 'Could not send verification code'), { code: payload.code });
      setOtpExpiresAt(payload.expiresAt || null);
      setOtpSent(true);
      setResendAvailableAt(Date.now() + 60_000);
    } catch (err: any) { handleStatusError(err); setOtpError(err.message || 'Could not send verification code'); }
  }, [handleStatusError, uuid]);

  if (error) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!data) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  const verifyOtp = async () => {
    setOtpError(null);
    try {
      const response = trackPublicPsfResponse(await fetch(withNgrokBrowserBypass(publicPsfApiUrl(`/api/super-personalized-psf-public/share/${encodeURIComponent(uuid)}/otp/verify`)), {
        method: 'POST',
        headers: withNgrokBrowserBypassHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ code: otp }),
      }));
      const payload = await readJsonResponse(response);
      if (!response.ok) throw Object.assign(new Error(payload.error || 'Invalid or expired code'), { code: payload.code });
      const verifiedSession = payload.sessionToken || null;
      publicSessionTokenRef.current = verifiedSession;
      setSessionToken(verifiedSession);
      if (verifiedSession) configurePublicPsfSession({ uuid, sessionToken: verifiedSession });
      setData((current) => current ? { ...current, activatedAt: payload.activatedAt || new Date().toISOString() } : current);
    } catch (err: any) { handleStatusError(err); setOtpError(err.message || 'Invalid or expired code'); }
  };

  const agentInitials = (name: string | null | undefined, ceaNumber: string | null | undefined) => {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (ceaNumber || 'AG').slice(0, 2).toUpperCase();
  };

  const pageBackground = darkMode ? '#111722' : '#eef3fb';
  const pageText = darkMode ? '#fff' : '#172038';
  const cardBackground = darkMode ? '#1a2230' : '#fff';
  const mutedText = darkMode ? '#b8c0cc' : '#5f687c';
  const agent = data.invitedBy;
  const agentPhoto = buildAgentPhotoUrl(agent.photo);
  const agentCard = <Box sx={{ mt: 3, p: { xs: 2.5, md: 4 }, borderRadius: 3, bgcolor: cardBackground, color: pageText, textAlign: 'left', boxShadow: darkMode ? 'none' : '0 2px 10px rgba(20,30,50,.08)' }}>
    <Typography sx={{ fontWeight: 800, letterSpacing: 1.2, mb: 3 }}>CONTACT YOUR AGENT</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}><Avatar src={agentPhoto} sx={{ width: 64, height: 64, bgcolor: '#f58b2a', color: '#fff', fontWeight: 800 }}>{agentInitials(agent.name, agent.ceaNumber)}</Avatar><Box><Typography sx={{ fontSize: { xs: '1.35rem', md: '1.8rem' }, fontWeight: 800 }}>{agent.name || agent.ceaNumber}</Typography><Typography sx={{ fontSize: '1rem', color: mutedText }}>Huttons Asia Pte Ltd</Typography></Box></Box>
    <Divider sx={{ borderColor: darkMode ? '#303847' : '#d8dde6', mb: 3 }} />
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}><BadgeIcon sx={{ color: '#f58b2a' }} /><Box><Typography sx={{ fontWeight: 800 }}>CEA NUMBER</Typography><Typography sx={{ fontSize: '1.15rem' }}>{agent.ceaNumber || '—'}</Typography></Box></Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}><PhoneIcon sx={{ color: '#f58b2a' }} /><Box><Typography sx={{ fontWeight: 800 }}>PHONE</Typography><Typography sx={{ color: '#f58b2a', fontSize: '1.15rem' }}>{agent.mobile || '—'}</Typography></Box></Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}><EmailIcon sx={{ color: '#f58b2a' }} /><Box><Typography sx={{ fontWeight: 800 }}>EMAIL</Typography><Typography sx={{ color: '#f58b2a', fontSize: '1.15rem', overflowWrap: 'anywhere' }}>{agent.email || '—'}</Typography></Box></Box>
    </Box>
  </Box>;

  if (!verified && !expired) {
    return <Box sx={{ minHeight: '100dvh', bgcolor: darkMode ? '#111722' : '#eef3fb', color: pageText, display: 'grid', placeItems: 'center', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ width: '100%', maxWidth: 820, bgcolor: darkMode ? '#1a2230' : '#fff', borderRadius: 3, p: { xs: 2.5, sm: 5 }, boxShadow: darkMode ? 'none' : '0 4px 24px rgba(20,30,50,.12)' }}>
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}><WhatsAppIcon sx={{ color: '#25D366', fontSize: 36 }} /><Typography variant="h5" sx={{ fontWeight: 800 }}>Verify your phone</Typography></Box>
        <Typography sx={{ mt: 1.5, color: mutedText }}>For your protection, this link requires a one-time code sent via WhatsApp to {data.consumer.mobileMasked || 'your number'}.</Typography>
        {otpSent && <Alert severity="success" sx={{ mt: 2 }}>Code sent via WhatsApp to {data.consumer.mobileMasked || 'your number'}. It expires in 10 minutes{otpExpiresAt ? ` (at ${new Date(otpExpiresAt).toLocaleTimeString()})` : ''}.</Alert>}
        {otpError && <Alert severity="error" sx={{ mt: 2 }}>{otpError}</Alert>}
        <TextField fullWidth autoFocus label="6-digit code" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', maxLength: 6 }} sx={{ mt: 2, '& .MuiOutlinedInput-root': { fontSize: '1.5rem', letterSpacing: 6 } }} />
        <Button fullWidth variant="contained" disabled={!otpSent || otp.length !== 6} sx={{ mt: 2 }} onClick={() => void verifyOtp()}>VERIFY AND CONTINUE</Button>
        {otpSent && <Button fullWidth variant="text" disabled={resendSeconds > 0} onClick={() => void sendOtp()} sx={{ mt: 1, color: resendSeconds > 0 ? mutedText : '#f58b2a' }}>{resendSeconds > 0 ? `RESEND IN ${resendSeconds}s` : 'RESEND CODE'}</Button>}
        {beforeAccess && <Typography sx={{ mt: 2, textAlign: 'center', color: mutedText }}>Event opens in {accessOpenCountdown}</Typography>}
        <Typography sx={{ mt: 2, textAlign: 'center', color: mutedText, fontSize: '0.85rem' }}>Didn&apos;t receive it? Check that WhatsApp is installed on the invited number.</Typography>
      </Box>
    </Box>;
  }

  return <Box sx={{ height: '100dvh', bgcolor: pageBackground, color: pageText, overflow: 'hidden' }}>
    <Box sx={{ height: 64, px: { xs: 1, sm: 2, md: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, bgcolor: darkMode ? '#1a202c' : '#fff', borderBottom: `1px solid ${darkMode ? '#303847' : '#d8dde6'}`, position: 'sticky', top: 0, zIndex: 10 }}>
      <Box component="img" src={darkMode ? HuttonsWhite : HuttonsBlue} alt="Huttons" sx={{ height: { xs: 22, sm: 28 }, width: 'auto', opacity: 0.9, flexShrink: 0 }} />
      <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: mutedText, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>| Personalized PSF</Typography>
      <Typography sx={{ flex: 1, minWidth: 0, textAlign: 'center', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.event.name}</Typography>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, fontSize: '0.72rem', color: mutedText, whiteSpace: 'nowrap' }}><span>{eventEnded ? 'Event Ended' : beforeStart ? `Event Starts In: ${eventCountdown}` : 'Event Started'}</span><span>·</span><span>{expired ? 'Access Expired' : boundaryCheckPending && !beforeAccess ? 'Checking access…' : `Access Expires In: ${accessCountdown}`}</span></Box>
      <IconButton size="small" onClick={() => setTheme(darkMode ? 'light' : 'dark')} sx={{ color: pageText }} aria-label="Toggle theme">{darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}</IconButton>
      <Button size="small" endIcon={<ExpandMoreIcon />} onClick={(event) => setAnchor(event.currentTarget)} sx={{ color: pageText, textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0.5 }}><Avatar src={agentPhoto} sx={{ width: 30, height: 30, mr: 0.5, bgcolor: '#f58b2a', color: '#fff', fontSize: '0.75rem', fontWeight: 800 }}>{agentInitials(agent.name, agent.ceaNumber)}</Avatar><Box sx={{ display: { xs: 'none', sm: 'block' } }}>{agent.name || agent.ceaNumber}</Box></Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}><MenuItem disabled>{agent.name || agent.ceaNumber}</MenuItem><MenuItem disabled>Huttons Asia Pte Ltd</MenuItem><MenuItem disabled>CEA: {agent.ceaNumber || '—'}</MenuItem><MenuItem disabled>Phone: {agent.mobile || '—'}</MenuItem><MenuItem disabled>Email: {agent.email || '—'}</MenuItem></Menu>
    </Box>
    <Box sx={{ height: 'calc(100dvh - 64px)', overflow: 'auto', px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto', textAlign: 'center' }}>
        {expired ? <Box sx={{ mt: 2, p: { xs: 2, md: 4 }, borderRadius: 2, bgcolor: darkMode ? '#1d1206' : '#fff8e7', color: darkMode ? '#ffe5b4' : '#6d4b16', textAlign: 'left' }}><Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}><WarningAmberIcon sx={{ color: '#f5a126' }} /><Box><Typography variant="h5" sx={{ fontWeight: 800 }}>Your trial period has ended.</Typography><Typography sx={{ mt: 1, color: mutedText }}>This share link was valid for a limited preview window which has now ended. Please contact your agent for a new link.</Typography></Box></Box></Box> : beforeAccess ? <Box sx={{ mt: 2 }}><Typography sx={{ color: mutedText }}>Event opens in {accessOpenCountdown}</Typography></Box> : <Box sx={{ mt: 2, textAlign: 'left', height: 'calc(100dvh - 190px)', minHeight: 520, display: 'flex', flexDirection: 'column' }}><GlobalCalculatorSettingsProvider><PsfDashboard reportContext={{ userInfo: { Name: agent.name || undefined, Email: agent.email || undefined, Mobile: agent.mobile || undefined, CEANumber: agent.ceaNumber || undefined, Photo: agent.photo || undefined }, consumerName: data.consumer.name }} /></GlobalCalculatorSettingsProvider></Box>}
        {(expired || beforeAccess) && agentCard}
      </Box>
    </Box>
  </Box>;
}
